<script>
import {ExhibitorMixin} from "../lib/speaker-utils.js"
import Favorite from "../lib/favorite.vue"

export default {
  mixins: [ExhibitorMixin],
  components: {Favorite},
  props: {
    exhibitorType: {type: String, default: 'exhibitor'},
    exhibitor: {type: Object, required: true},
    listDisplay: {type: Boolean, default: true},
  },

  data() {
    return {
      descriptionVisible: false,
      emailVisible: false,
      selectedImageIndex: 0
    }
  },

  computed: {
    userIsLoggedIn() {
      return this.$storage.getToken()
    },

    hasProfileImages() {
      return this.exhibitor.profile_images && this.exhibitor.profile_images.length > 0
    },

    displayImage() {
      // In list display: always show logo
      if (this.listDisplay) {
        return this.exhibitor.logo
      }

      // In detail display: show profile images if available
      if (this.hasProfileImages) {
        return this.exhibitor.profile_images[this.selectedImageIndex].image
      }
      return this.exhibitor.logo
    },

    showImageGallery() {
      // Only show image gallery in detail view and when images exist
      return !this.listDisplay && this.hasProfileImages && this.exhibitor.profile_images.length > 1
    },

    showLogoInHeader() {
      // Show small logo in header when in detail view and images exist
      return !this.listDisplay && this.hasProfileImages
    }
  },

  created() {
  },

  methods: {
    mailToLink() {
      return `mailto:${this.exhibitor.email}`
    },
    website(url) {
      if (!url) return url
      if (url.startsWith('http')) return url
      return 'https://' + url
    },
    selectImage(index) {
      this.selectedImageIndex = index
    },
  },


}
</script>

<template>
    <div class="row">
          <div :class="listDisplay ? 'col-3 col-lg-2' : 'col-5 col-lg-4'">
            <!-- Main Image Display -->
            <div class="ratio ratio-1x1 border rounded bg-light mb-2">
              <img v-if="displayImage" class="object-fit-contain p-1" :src="displayImage" :alt="exhibitor.company_name">
              <img v-else class="object-fit-contain p-1" src="/media/app/company-symbol.png" alt="company symbol" />
            </div>

            <!-- Thumbnail Gallery (only show in detail view with multiple images) -->
            <div v-if="showImageGallery" class="mt-2">
              <div class="row g-2">
                <div
                  v-for="(img, index) in exhibitor.profile_images"
                  :key="index"
                  class="col-4 col-md-3">
                  <div class="ratio ratio-1x1">
                    <img
                      :src="img.image"
                      :alt="img.title || exhibitor.company_name"
                      class="thumbnail-preview object-fit-cover rounded"
                      :class="{'thumbnail-active': selectedImageIndex === index}"
                      @click="selectImage(index)">
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div :class="listDisplay ? 'col-9 col-lg-10' : 'col-7 col-lg-8'">
            <div class="row">
              <div class="col-12 mb-3">
                <div class="d-flex align-items-center">
                  <!-- Small logo in header when images exist in detail view -->
                  <img
                    v-if="showLogoInHeader && exhibitor.logo"
                    :src="exhibitor.logo"
                    :alt="exhibitor.company_name"
                    class="logo-small me-2 border rounded p-1 bg-white">

                  <div>
                    <router-link v-if="listDisplay" :to="exhibitorUrl(exhibitor)">
                      {{ exhibitor.company_name }}
                    </router-link>
                    <span v-else>{{ exhibitor.company_name }}</span>
                    &nbsp;
                    <favorite :attr="exhibitorType" :value="exhibitor.sfid" :obj="exhibitor"
                     @favorite-changed="$emit('favorite-changed', $event)" />
                  </div>
                </div>

                <!-- Slogan (always visible if present) -->
                <p v-if="exhibitor.slogan" class="text-primary fw-bold mb-2">
                  {{ exhibitor.slogan }}
                </p>

                <!-- Short Description (only in detail view) -->
                <p v-if="!listDisplay && exhibitor.short_description" class="text-muted fst-italic mb-2">
                  {{ exhibitor.short_description }}
                </p>

                <p v-if="exhibitor.address">{{ exhibitor.address }}</p>

                <!-- Halle/Stand inline -->
                <p v-if="exhibitor.hall || exhibitor.stand" class="text-muted mb-2">
                  <span v-if="exhibitor.hall">Halle: {{ exhibitor.hall }}</span>
                  <span v-if="exhibitor.hall && exhibitor.stand"> | </span>
                  <span v-if="exhibitor.stand">Stand: {{ exhibitor.stand }}</span>
                </p>

                <div v-if="exhibitor.type != 'Exhibitor'" class="c-title pb-2">
                  <i class="fas fa-trophy"></i>&nbsp;{{ exhibitor.type }}
                </div>

                <span v-if="userIsLoggedIn && exhibitor.email">
                <button @click="emailVisible=true" class="btn btn-sm btn-secondary">Kontakt</button>
                <a v-if="emailVisible" :href="mailToLink()" class="mail-link">per Mail.</a>
                </span>

                <!--div v-if="exhibitor.website"><i class="fas fa-globe"></i>&nbsp;<a :href="website(exhibitor.website)" target="_blank">{{ exhibitor.website }}</a></div-->
              </div>

              <div v-if="exhibitor.description && listDisplay"
               class="col col-12 mb-3 d-none d-lg-block"
               v-html="exhibitor.description">
              </div>
            </div>
          </div>

          <div v-if="exhibitor.description && listDisplay" class="col col-12 pt-3 d-lg-none">
            <a href="#" class="btn btn-outline float-end"
            @click.prevent="descriptionVisible=!descriptionVisible">
              <i v-if="descriptionVisible" class="fa fa-chevron-down"></i>
              <i v-else class="fa fa-chevron-right"></i>
            </a>
            <div v-if="descriptionVisible" v-html="exhibitor.description"></div>
            <div v-else class="c-truncate" v-html="exhibitor.description"></div>
          </div>

          <div v-if="exhibitor.description && !listDisplay" class="col col-12 pt-3"
            v-html="exhibitor.description">
          </div>

      <slot></slot>
    </div>
</template>

<style scoped>

.c-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.c-session {
  border-top: 1px solid lightgrey;
}

hr {
  border-color: lightgrey;
}

.avatar {
  height:100px;
  width:100px;
  object-fit:cover;
  border-radius:50%;
  overflow:hidden;
}

.mail-link {
  text-decoration: underline;
}

/* Thumbnail Gallery Styles */
.thumbnail-preview {
  object-fit: cover;
  border: 2px solid transparent;
  border-radius: 4px;
  transition: border-color 0.2s, transform 0.2s;
}

.thumbnail-preview:hover {
  border-color: #dee2e6;
  transform: scale(1.05);
}

.thumbnail-active {
  border-color: #0d6efd !important;
  transform: scale(1.05);
}

/* Small logo in header - responsive size */
.logo-small {
  width: clamp(40px, 5vw, 80px);
  height: clamp(40px, 5vw, 80px);
  object-fit: contain;
}

</style>
